import React, { useState, useEffect, useCallback } from 'react';
import './PatientList.css';

const PatientList = ({ onPatientSelect }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  // Load all patients from backend
  const loadPatients = useCallback(async () => {
    try {
      setLoading(true);

      console.log('Backend URL:', BACKEND_URL);

      const response = await fetch(`${BACKEND_URL}/api/studies`);
      if (!response.ok) {
        throw new Error(`Failed to fetch patients: ${response.statusText}`);
      }
      const data = await response.json();
      setPatients(data.studyList || []);
      console.log('Patients successfully fetched from backend.');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error fetching patients:', errorMsg);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  // Load patients on start
  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // Reload patients from backend
  const handleReload = (async () => {
    setLoading(true);
    console.log('Reload clicked.');
    await loadPatients();
    console.log('Reload triggered: Refresh patients list.');
    setLoading(false);
  });


  // Handle patient row click
  const handlePatientClick = (patient) => {
    if (onPatientSelect) {
      onPatientSelect(patient);
    }
  };

  // Avoid duplicate patients with the same patientId
  const uniquePatients = React.useMemo(() => {
    const seen = new Map();
    patients.forEach(patient => {
      if (!seen.has(patient.patientId)) {
        seen.set(patient.patientId, patient);
      }
    });
    return Array.from(seen.values());
  }, [patients]);

  // Filter patients based on search term
  const filteredPatients = uniquePatients.filter(patient => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      patient.patientId?.toLowerCase().includes(searchLower) ||
      patient.patientName?.toLowerCase().includes(searchLower) ||
      patient.modality?.toLowerCase().includes(searchLower)
    );
  });

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredPatients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPatients = filteredPatients.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div id="main">
      <div className="patientListHeader">
        { /* Patients Label */}
        <span className="patients-label">
          Patients
          <span> </span>
          <span className="patients-count">{uniquePatients.length}</span>
        </span>
        { /* Search Bar */}
        <input
          type="text"
          className="search-bar"
          placeholder="Search patients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        { /* Reload Button */}
        <button className='patientList-reload-button' onClick={handleReload}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="#ffffff"><path d="M2 12a9 9 0 0 0 9 9c2.39 0 4.68-.94 6.4-2.6l-1.5-1.5A6.7 6.7 0 0 1 11 19c-6.24 0-9.36-7.54-4.95-11.95S18 5.77 18 12h-3l4 4h.1l3.9-4h-3a9 9 0 0 0-18 0" /></svg>
        </button>
      </div>
      { /* Patient List */}
      <div className="patientListContainer">
        {loading ? (
          <p>Loading patients...</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Modality</th>
                <th>Description</th>
                <th># Images</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody id="patientListData">
              {currentPatients.map((patient, index) => (
                <tr
                  key={index}
                  onClick={() => handlePatientClick(patient)}
                  className="patientList-table"
                >
                  <td>{patient.patientId}</td>
                  <td>{patient.patientName}</td>
                  <td>{patient.modality}</td>
                  <td>{patient.studyDescription}</td>
                  <td>{patient.numImages}</td>
                  <td>{patient.studyDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {!loading && filteredPatients.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
              ) : (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              )
            ))}

            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>

            <span className="pagination-info">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredPatients.length)} of {filteredPatients.length}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientList;
